import { useState, useEffect } from "react";
import toast from "../../../../utils/toast";
import {
  getPaymentGatewayRouting,
  masterPaymentGatewayList,
  updatePaymentGatewayRouting,
  togglePayGatewayRoutingIsActive,
} from "../../../../services/admin/adminServices";

export default function GatewayRouting() {
  const [isCardData, setCardData] = useState({});
  const [routingList, setRoutingList] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [gatewayTypes, setGatewayTypes] = useState([]);

  useEffect(() => {
    fetchRoutingList();
    fetchGatewayTypes();
  }, []);

  const fetchRoutingList = async () => {
    setListLoading(true);
    try {
      const res = await getPaymentGatewayRouting();
      if (res.statusCode === 1) {
        setRoutingList(res.data.routes);
        setCardData(res.data);
      }
    } catch (err) {
      console.log("Failed to fetch routing list");
    } finally {
      setListLoading(false);
    }
  };

  const fetchGatewayTypes = async () => {
    try {
      const res = await masterPaymentGatewayList();
      if (res.statusCode === 1) setGatewayTypes(res.data);
    } catch (err) {
      console.log("Failed to fetch gateway types");
    }
  };

  const updateRoutingRow = (id, field, value) => {
    setRoutingList((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)),
    );
  };

  const handleSaveRoute = async (route) => {
    try {
      const payload = {
        id: route.id,
        primaryGatewayId: route.primaryGatewayId,
        fallbackGatewayId: route.fallbackGatewayId || 0,
        minSuccessRate: route.minSuccessRate,
      };
      const res = await updatePaymentGatewayRouting(payload);
      if (res.statusCode === 1) {
        toast.success(res.message || "Route updated successfully.");
      } else {
        toast.error(res.message || "Failed to update route.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    }
  };

  const handleToggleRoute = async (route) => {
    const newValue = !route.isActive;
    updateRoutingRow(route.id, "isActive", newValue);
    try {
      const res = await togglePayGatewayRoutingIsActive(route.id, newValue);
      if (res.statusCode === 1) {
        toast.success(res.message);
      } else {
        toast.error(res.message || "Failed to toggle.");
        updateRoutingRow(route.id, "isActive", !newValue);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
      updateRoutingRow(route.id, "isActive", !newValue);
    }
  };

  const handleSaveAllRoutes = async () => {
    try {
      for (const route of routingList) {
        await updatePaymentGatewayRouting({
          id: route.id,
          primaryGatewayId: route.primaryGatewayId,
          fallbackGatewayId: route.fallbackGatewayId || 0,
          minSuccessRate: route.minSuccessRate,
        });
      }
      toast.success("All routes saved successfully.");
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    }
  };
  return (
    <>
      <div className="main">
        <div className="topbar">
          <div>
            <div className="page-title" id="page-title">
              Gateway Routing
            </div>
            <div className="page-sub" id="page-sub">
              System › Route payment modes to gateways
            </div>
          </div>
          <div className="topbar-right" id="topbar-right" />
        </div>
        <div className="content" id="content-area">
          <div className="stat-grid">
            <div className="stat-card">
              <div className="stat-icon">💳</div>
              <div className="stat-label">Total Gateways</div>
              <div className="stat-value">{isCardData?.totalGateways}</div>
              <div className="stat-delta delta-neu">Configured</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">✅</div>
              <div className="stat-label">Active Gateways</div>
              <div className="stat-value">{isCardData?.activeGateways}</div>
              <div className="stat-delta delta-up">Processing payments</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🔀</div>
              <div className="stat-label">Routing Rules</div>
              <div className="stat-value">{isCardData?.routingRules}</div>
              <div className="stat-delta delta-up">Mode-based routes</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">📊</div>
              <div className="stat-label">Success Rate</div>
              <div className="stat-value">{isCardData?.successRate7Day}%</div>
              <div className="stat-delta delta-up">Last 7 days</div>
            </div>
          </div>
          <div className="card mb16">
            <div className="flex-between mb12">
              <div>
                <div className="card-title" style={{ margin: 0 }}>
                  Gateway Routing Rules
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: "var(--text-secondary)",
                    marginTop: 3,
                  }}
                >
                  Route each payment mode to a primary gateway with an automatic
                  fallback if the primary fails.
                </div>
              </div>
              <button className="btn btn-primary" onClick={handleSaveAllRoutes}>
                💾 Save All Routes
              </button>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table>
                <thead>
                  <tr>
                    <th style={{ minWidth: 180 }}>Payment Mode</th>
                    <th style={{ width: 90 }}>Enabled</th>
                    <th style={{ minWidth: 180 }}>Primary Gateway</th>
                    <th style={{ minWidth: 180 }}>Fallback Gateway</th>
                    <th style={{ minWidth: 160 }}>Min. Success Rate</th>
                    <th style={{ minWidth: 100 }}>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {listLoading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i}>
                        {Array.from({ length: 6 }).map((_, j) => (
                          <td key={j}>
                            <div
                              style={{
                                height: 14,
                                borderRadius: 6,
                                background:
                                  "linear-gradient(90deg, var(--bg-secondary) 25%, var(--border-light) 50%, var(--bg-secondary) 75%)",
                                backgroundSize: "200% 100%",
                                animation: "shimmer 1.4s infinite",
                                width: "70%",
                              }}
                            />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : routingList.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        style={{
                          textAlign: "center",
                          color: "var(--text-tertiary)",
                          padding: 24,
                        }}
                      >
                        No routing rules found.
                      </td>
                    </tr>
                  ) : (
                    routingList.map((route) => (
                      <tr key={route.id}>
                        <td>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <span style={{ fontSize: 16 }}>{route.icon}</span>
                            <div>
                              <div style={{ fontWeight: 600, fontSize: 13 }}>
                                {route.modeName}
                              </div>
                              <div
                                style={{
                                  fontSize: 10,
                                  color: "var(--text-tertiary)",
                                }}
                              >
                                {route.modeDescription}
                              </div>
                            </div>
                          </div>
                        </td>

                        <td>
                          <label
                            className="toggle"
                            style={{ display: "inline-block" }}
                          >
                            <input
                              type="checkbox"
                              checked={route.isActive}
                              onChange={() => handleToggleRoute(route)}
                            />
                            <span className="toggle-slider" />
                          </label>
                        </td>
                        <td>
                          <select
                            style={{
                              fontSize: 12,
                              padding: "5px 8px",
                              width: 160,
                            }}
                            value={route.primaryGatewayId}
                            onChange={(e) =>
                              updateRoutingRow(
                                route.id,
                                "primaryGatewayId",
                                Number(e.target.value),
                              )
                            }
                          >
                            <option value="">-- Select Primary --</option>
                            {gatewayTypes.map((g) => (
                              <option
                                key={g.paymentGatewayTypeID}
                                value={g.paymentGatewayTypeID}
                              >
                                {g.paymentGatewayType}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <select
                            style={{
                              fontSize: 12,
                              padding: "5px 8px",
                              width: 160,
                            }}
                            value={route.fallbackGatewayId || 0}
                            onChange={(e) =>
                              updateRoutingRow(
                                route.id,
                                "fallbackGatewayId",
                                Number(e.target.value),
                              )
                            }
                          >
                            <option value={0}>-- No Fallback --</option>
                            {gatewayTypes.map((g) => (
                              <option
                                key={g.paymentGatewayTypeID}
                                value={g.paymentGatewayTypeID}
                              >
                                {g.paymentGatewayType}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <input
                            type="number"
                            value={route.minSuccessRate}
                            min={0}
                            max={100}
                            style={{
                              fontSize: 12,
                              padding: "5px 8px",
                              width: 70,
                            }}
                            onChange={(e) =>
                              updateRoutingRow(
                                route.id,
                                "minSuccessRate",
                                Number(e.target.value),
                              )
                            }
                          />
                          <span
                            style={{
                              fontSize: 11,
                              color: "var(--text-secondary)",
                            }}
                          >
                            % min success
                          </span>
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: 4 }}>
                            <button
                              className="btn btn-xs btn-primary"
                              onClick={() => handleSaveRoute(route)}
                            >
                              Save
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <div className="two-col">
            <div className="card">
              <div className="card-title">Routing Logic</div>
              <div
                style={{
                  fontSize: 12,
                  color: "var(--text-secondary)",
                  lineHeight: "1.8",
                }}
              >
                <p style={{ marginBottom: 8 }}>
                  When a customer initiates a payment:
                </p>
                <ol
                  style={{
                    paddingLeft: 18,
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                  }}
                >
                  <li>
                    Platform detects the <strong>payment mode</strong> (UPI, CC,
                    DC, Netbanking, etc.)
                  </li>
                  <li>
                    Checks if routing is <strong>enabled</strong> for that mode
                  </li>
                  <li>
                    Attempts the transaction via the
                    <strong>Primary Gateway</strong>
                  </li>
                  <li>
                    If the primary gateway fails or success rate drops below
                    threshold → automatically retries via
                    <strong>Fallback Gateway</strong>
                  </li>
                  <li>
                    Transaction result is logged for success rate tracking
                  </li>
                </ol>
              </div>
            </div>

            <div className="card">
              <div className="card-title">
                Gateway Success Rates (Last 7 Days)
              </div>
              {isCardData?.gatewaySuccessRates?.map((g) => {
                const rate = g.successRate ?? 0;
                const color =
                  rate >= 95 ? "#3B6D11" : rate >= 85 ? "#BA7517" : "#A32D2D";
                return (
                  <div key={g.gatewayId} style={{ marginBottom: 10 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: 12,
                        marginBottom: 3,
                      }}
                    >
                      <span>{g.gatewayName}</span>
                      <span style={{ fontWeight: 600, color }}>{rate}%</span>
                    </div>
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{ width: `${rate}%`, background: color }}
                      />
                    </div>
                  </div>
                );
              })}

              {isCardData?.gatewaySuccessRates?.length === 0 && (
                <div style={{ fontSize: 12, color: "var(--text-tertiary)" }}>
                  No data available.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
